import { Analytics } from '@analytics/Analytics';
import { EventNames } from '@analytics/constants';
import { ApiCoreClient } from '@api/ApiCoreClient';
import { IncomingRoomStateUpdatedMessage } from '@api/roomState/types/socketProtocol';
import { ExtendedTemplateData } from '@api/types/templateConfig';
import { RoomDetailsStateShape } from '@api/roomState/types/common';
import { Bounds, Vector2 } from '@src/types/spatials';

import { ApiSubClient } from './ApiSubClient';

export class RoomStateClient extends ApiSubClient {
  constructor(core: ApiCoreClient) {
    super(core);
    core.socket.on('message:roomStateUpdated', this.onRoomStateUpdated);
  }

  private onRoomStateUpdated = (message: IncomingRoomStateUpdatedMessage) => {
    this.core.cacheApi.updateRoomState(message.payload);
  };

  bringToFront = (payload: { widgetId: string }) => {
    this.core.cacheApi.bringToFront(payload);
    this.core.socket.send({
      kind: 'updateRoomState',
      // TODO: partial updates for room state? Might already work?
      payload: this.core.roomStateStore.getState().state,
    });
  };

  setIsAudioGlobal = (isAudioGlobal: boolean) => {
    this.core.cacheApi.updateRoomState({ isAudioGlobal });
    this.core.socket.send({
      kind: 'updateRoomState',
      // retrieve the full state for sending to the socket
      payload: this.core.roomStateStore.getState().state,
    });
    Analytics.trackEvent(isAudioGlobal ? EventNames.ENABLED_GLOBAL_AUDIO : EventNames.DISABLED_GLOBAL_AUDIO, null, {
      roomId: this.core.roomStateStore.getState().id,
    });
  };

  setWallpaperScale = (wallpaperScale: number) => {
    this.core.cacheApi.updateRoomState({ wallpaperScale });
    this.core.socket.send({
      kind: 'updateRoomState',
      payload: this.core.roomStateStore.getState().state,
    });
  };

  setWallpaperRepeats = (wallpaperRepeats: boolean) => {
    this.core.cacheApi.updateRoomState({
      wallpaperRepeats,
      wallpaperMode: wallpaperRepeats ? 'tile' : 'centered',
    });
    this.core.socket.send({
      kind: 'updateRoomState',
      // retrieve the full state for sending to the socket
      payload: this.core.roomStateStore.getState().state,
    });
  };

  setWallpaperDisplayMode = (wallpaperMode: 'centered' | 'tile') => {
    this.core.cacheApi.updateRoomState({
      wallpaperMode,
      wallpaperRepeats: wallpaperMode === 'tile',
    });
    this.core.socket.send({
      kind: 'updateRoomState',
      // retrieve the full state for sending to the socket
      payload: this.core.roomStateStore.getState().state,
    });
  };

  setIneaSettings = (settings: {
    ineaChatEnabled?: boolean;
    ineaPrivateEnabled?: boolean;
    ineaPrivateQuota?: number;
    ineaPrivatePlan?: string;
    ineaPublishedInstructions?: string;
    ineaActiveStep?: string;
    ineaPlanVersion?: number;
    ineaVoiceEnabled?: boolean;
    ineaVoiceBackend?: 'browser' | 'agent' | 'auto';
    ineaLanguage?: 'auto' | 'fr' | 'en' | 'ar';
  }) => {
    this.core.cacheApi.updateRoomState(settings);
    this.core.socket.send({
      kind: 'updateRoomState',
      payload: this.core.roomStateStore.getState().state,
    });
  };

  setVisualRoomSettings = (settings: {
    wallpaperScale?: number;
    wallpaperTileLimit?: number;
    templateConfig?: ExtendedTemplateData | null;
  }) => {
    const patch: any = {};
    if (settings.wallpaperScale !== undefined) {
      patch.wallpaperScale = Math.min(5, Math.max(0.1, Number(settings.wallpaperScale) || 1));
    }
    if (settings.wallpaperTileLimit !== undefined) {
      patch.wallpaperTileLimit = settings.wallpaperTileLimit;
    }
    if (settings.templateConfig !== undefined) {
      patch.templateConfig = settings.templateConfig ? JSON.stringify(settings.templateConfig) : undefined;
    }
    this.core.cacheApi.updateRoomState(patch);
    this.core.socket.send({
      kind: 'updateRoomState',
      payload: this.core.roomStateStore.getState().state,
    });
  };

  setTranscriptEnabled = (transcriptEnabled: boolean) => {
    this.core.cacheApi.updateRoomState({ transcriptEnabled });
    this.core.socket.send({
      kind: 'updateRoomState',
      payload: this.core.roomStateStore.getState().state,
    });
  };

  setTranscriptDefaults = (settings: {
    language?: RoomDetailsStateShape['transcriptDefaultLanguage'];
    targetLanguage?: string;
  }) => {
    this.core.cacheApi.updateRoomState({
      transcriptDefaultLanguage: settings.language,
      transcriptDefaultTargetLanguage: settings.targetLanguage,
    });
    this.core.socket.send({
      kind: 'updateRoomState',
      payload: this.core.roomStateStore.getState().state,
    });
  };

  setIneaTransform = (payload: { position?: Vector2; size?: Bounds }) => {
    this.core.cacheApi.updateRoomState({
      ineaPosition: payload.position,
      ineaSize: payload.size,
    });
    this.core.socket.send({
      kind: 'updateRoomState',
      payload: this.core.roomStateStore.getState().state,
    });
  };
}
